import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { BiDownload } from 'react-icons/bi';
import { FaTrash } from 'react-icons/fa';
import CheckDocument from 'src/models/personnel/CheckDocument';

interface ICheckDocumentManagementDialogProps {
    dialogName: string;
    hideDialog: () => void;
    checkNo?: number;
    paidTo?: string;
    periodNo?: number;
}

const formatFileSize = (bytes?: number): string => {
    if (!bytes) {
        return '';
    }
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Mirrors the Docs window (opened by double-clicking a check row, oid 66) - a flat per-check
// attachment list with Add/Update/Delete/Retrieve (oids 1102/1105/1108/1089), distinct from
// Save Stubs' single auto-generated "Original Check Stub" (same CHECK_STUB table, just no
// longer assumed to be exactly one row). No archiving or merge here - Oracle's own Docs window
// doesn't have either, and the user confirmed matching Oracle's real feature set over the
// (more complex) UI pattern this was modeled on, InvoiceDocumentManagementDialog.
const CheckDocumentManagementDialog = (props: ICheckDocumentManagementDialogProps) => {
    const { register, getValues, setValue } = useForm();
    const { get, post, put, del, response } = useFetch();
    const [documents, setDocuments] = useState<CheckDocument[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [editingDocId, setEditingDocId] = useState<number | undefined>(undefined);
    const [showForm, setShowForm] = useState<boolean>(false);
    // Mirrors InvoiceMaintenancePageComponent's selectedInvoiceIndex/yellow-row pattern - a
    // single click only selects/highlights a row now, it no longer opens the edit form by
    // itself. Editing requires either the Update button or a double-click.
    const [selectedDocId, setSelectedDocId] = useState<number | undefined>(undefined);

    const fetchDocuments = async () => {
        if (!props.checkNo) {
            return;
        }
        setLoading(true);
        const result = await get(`/v1.0/Payroll/${props.checkNo}/documents`);
        setLoading(false);
        if (Array.isArray(result)) {
            setDocuments(result);
        }
    };

    useEffect(() => {
        fetchDocuments();
        setShowForm(false);
        setEditingDocId(undefined);
        setSelectedDocId(undefined);
        setMessage('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.checkNo]);

    const handleAddNew = () => {
        setEditingDocId(undefined);
        setValue('description', '');
        setShowForm(true);
        setMessage('');
    };

    const handleUpdateSelected = () => {
        const doc = documents.find((d) => d.docId === selectedDocId);
        if (doc) {
            handleEdit(doc);
        }
    };

    const handleEdit = (doc: CheckDocument) => {
        setEditingDocId(doc.docId);
        setValue('description', doc.description ?? '');
        setShowForm(true);
        setMessage('');
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingDocId(undefined);
    };

    const handleSaveDocument = async () => {
        const values = getValues();
        const fileInput = document.getElementById('checkDocFile') as HTMLInputElement;
        const file = fileInput?.files?.[0];

        if (!editingDocId && !file) {
            setMessage('Please choose a file.');
            return;
        }

        const formData = new FormData();
        formData.append('description', values.description ?? '');
        if (file) {
            formData.append('file', file);
        }

        if (editingDocId) {
            await put(`/v1.0/Payroll/documents/${editingDocId}`, formData);
        } else {
            await post(`/v1.0/Payroll/${props.checkNo}/documents`, formData);
        }

        if (!response.ok) {
            setMessage('Unable to save the document. Please try again.');
            return;
        }

        setShowForm(false);
        setEditingDocId(undefined);
        fetchDocuments();
    };

    const handleDelete = async (doc: CheckDocument) => {
        if (!window.confirm('Are you sure you want to delete this file?')) {
            return;
        }
        await del(`/v1.0/Payroll/documents/${doc.docId}`);
        if (response.ok) {
            fetchDocuments();
        } else {
            setMessage('Unable to delete the document. Please try again.');
        }
    };

    const handleDownload = async (doc: CheckDocument) => {
        await get(`/v1.0/Payroll/documents/${doc.docId}/download`);
        if (!response.ok) {
            setMessage('Unable to download the document. Please try again.');
            return;
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = doc.fileOrig || `Document_${doc.docId}`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
    };

    return (
        <Dialog
            title={`Check Documents${props.checkNo ? ' - Check #' + props.checkNo : ''}`}
            dialogName={props.dialogName}
            isExitButtonInvisible={true}
            bodyStyle={{ width: '45%' }}
        >
            {(props.paidTo || props.periodNo) && (
                <div style={{ marginBottom: '0.5rem' }}>
                    {props.paidTo && <span>Paid To: <strong>{props.paidTo}</strong>&nbsp;&nbsp;&nbsp;</span>}
                    {props.periodNo && <span>Period #: <strong>{props.periodNo}</strong></span>}
                </div>
            )}
            <table style={{ width: '100%' }}>
                <thead style={{ textAlign: 'center' }}>
                    <tr>
                        <th>Description</th>
                        <th>Date</th>
                        <th>Size</th>
                        <th>Type</th>
                        <th style={{ width: '3rem' }} />
                        <th style={{ width: '3rem' }} />
                    </tr>
                </thead>
                <tbody>
                    {!loading && documents.map((doc) => (
                        <tr
                            key={doc.docId}
                            className="table-item-hover"
                            style={{ textAlign: 'center', backgroundColor: doc.docId === selectedDocId ? '#FFFF66' : '', cursor: 'pointer' }}
                            onClick={() => setSelectedDocId(doc.docId)}
                            onDoubleClick={() => handleEdit(doc)}
                        >
                            <td>{doc.description}</td>
                            <td>{doc.fileDate ? new Date(doc.fileDate).toLocaleDateString() : ''}</td>
                            <td>{formatFileSize(doc.fileSize)}</td>
                            <td>{doc.fileType}</td>
                            <td>
                                <Button
                                    style={{ height: '2.5rem', background: 'transparent' }}
                                    text=""
                                    title="Download"
                                    reactIcon={BiDownload}
                                    iconHoverColor="blue"
                                    iconStyle={{ width: '1.5rem', height: '1.5rem' }}
                                    onClick={() => handleDownload(doc)}
                                />
                            </td>
                            <td>
                                <Button
                                    style={{ height: '2.5rem', background: 'transparent' }}
                                    text=""
                                    title="Delete"
                                    reactIcon={FaTrash}
                                    iconHoverColor="blue"
                                    iconStyle={{ width: '1.25rem', height: '1.25rem' }}
                                    onClick={() => handleDelete(doc)}
                                />
                            </td>
                        </tr>
                    ))}
                    {!loading && documents.length === 0 && (
                        <tr><td colSpan={6} className="bold-centered">No documents</td></tr>
                    )}
                </tbody>
            </table>

            {showForm && (
                <div className="form-row" style={{marginLeft: '1rem', marginTop: '1rem', flexDirection: 'column', border: '1px solid #ccc', padding: '0.75rem' }}>
                    <RHFInputBox label="Description" name="description" type="text" register={register} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                    <div className="form-row" style={{ marginTop: '0.5rem' }}>
                        <label style={{ marginRight: '1rem' }}>{editingDocId ? 'Replace File (optional):' : 'File:'}</label>
                        <input id="checkDocFile" type="file" />
                    </div>
                    <div className="form-row" style={{ whiteSpace: 'nowrap', marginTop: '1rem', justifyContent: 'center' }}>
                        <Button className="blue-hover-button" style={{ width: 'auto', marginRight: '1rem' }} text={editingDocId ? 'Save Changes' : 'Add'} onClick={handleSaveDocument} />
                        <Button className="blue-hover-button" style={{ width: 'auto' }} text="Cancel" onClick={handleCancelForm} />
                    </div>
                </div>
            )}

            {!showForm && (
                <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                    <Button className="blue-hover-button" style={{ width: '25%', marginRight: '1rem' }} text="Add Document" onClick={handleAddNew} />
                    <Button className="blue-hover-button" style={{ width: '25%', marginRight: '1rem' }} text="Update" disabled={selectedDocId === undefined} onClick={handleUpdateSelected} />
                    <Button className="blue-hover-button" style={{ width: '25%' }} text="Close" onClick={() => props.hideDialog()} />
                </div>
            )}
            <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{message}</div>
        </Dialog>
    );
};

export default CheckDocumentManagementDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/CheckDocumentManagementDialog.tsx